import {useCallback, useMemo, useState} from 'react'
import {
  type GestureResponderEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {PlatformInfo} from '@bsky.app/expo-bluesky-swiss-army'
import {plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'
import {useNavigation, useNavigationState} from '@react-navigation/native'

import {UNREAD_NOTIFICATION_CAP} from '#/lib/constants'
import {useAccountSwitcher} from '#/lib/hooks/useAccountSwitcher'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {
  getCurrentRoute,
  getTabState,
  isCurrentProfileRoute,
  isTab,
  TabState,
} from '#/lib/routes/helpers'
import {makeProfileLink} from '#/lib/routes/links'
import {type SidebarTab} from '#/lib/routes/tab-to-nav-item'
import {
  type CommonNavigatorParams,
  type NavigationProp,
} from '#/lib/routes/types'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {isInvalidHandle, sanitizeHandle} from '#/lib/strings/handles'
import {emitSoftReset} from '#/state/events'
import {useHomeBadge} from '#/state/home-badge'
import {useFetchHandle} from '#/state/queries/handle'
import {useUnreadMessageCount} from '#/state/queries/messages/list-conversations'
import {useModerationInboxUnreadCountQuery} from '#/state/queries/moderation-inbox'
import {useUnreadNotifications} from '#/state/queries/notifications/unread'
import {useProfilesQuery} from '#/state/queries/profile'
import {type SessionAccount, useSession, useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {useCloseAllActiveElements} from '#/state/util'
import {LoadingPlaceholder} from '#/view/com/util/LoadingPlaceholder'
import {PressableWithHover} from '#/view/com/util/PressableWithHover'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {NavSignupCard} from '#/view/shell/NavSignupCard'
import {
  atoms as a,
  native,
  tokens,
  useBreakpoints,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {type DialogControlProps} from '#/components/Dialog'
import {ArrowBoxLeft_Stroke2_Corner0_Rounded as LeaveIcon} from '#/components/icons/Arrow'
import {
  Bell_Filled_Corner0_Rounded as BellFilledIcon,
  Bell_Stroke2_Corner0_Rounded as BellIcon,
} from '#/components/icons/Bell'
import {
  Bookmark_Filled_Corner0_Rounded as BookmarkFilledIcon,
  Bookmark_Stroke2_Corner0_Rounded as BookmarkIcon,
} from '#/components/icons/Bookmark'
import {
  BulletList_Filled_Corner0_Rounded as ListFilledIcon,
  BulletList_Stroke2_Corner0_Rounded as ListIcon,
} from '#/components/icons/BulletList'
import {type Props as SVGIconProps} from '#/components/icons/common'
import {DotGrid3x1_Stroke2_Corner0_Rounded as EllipsisIcon} from '#/components/icons/DotGrid'
import {Earth_Stroke2_Corner0_Rounded as EarthIcon} from '#/components/icons/Earth'
import {EditBig_Stroke2_Corner2_Rounded as EditBigIcon} from '#/components/icons/EditBig'
import {
  Hashtag_Filled_Corner0_Rounded as HashtagFilledIcon,
  Hashtag_Stroke2_Corner0_Rounded as HashtagIcon,
} from '#/components/icons/Hashtag'
import {
  HomeOpen_Filled_Corner0_Rounded as HomeFilledIcon,
  HomeOpen_Stroke2_Corner0_Rounded as HomeIcon,
} from '#/components/icons/Home'
import {
  MagnifyingGlass_Filled_Stroke2_Corner0_Rounded as MagnifyingGlassFilledIcon,
  MagnifyingGlass_Stroke2_Corner0_Rounded as MagnifyingGlassIcon,
} from '#/components/icons/MagnifyingGlass'
import {
  Message_Stroke2_Corner0_Rounded as MessageIcon,
  Message_Stroke2_Corner0_Rounded_Filled as MessageFilledIcon,
} from '#/components/icons/Message'
import {
  Newspaper_Filled_Corner2_Rounded as NewspaperFilledIcon,
  Newspaper_Stroke2_Corner2_Rounded as NewspaperIcon,
} from '#/components/icons/Newspaper'
import {PlusLarge_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {
  SettingsGear2_Filled_Corner0_Rounded as SettingsFilledIcon,
  SettingsGear2_Stroke2_Corner0_Rounded as SettingsIcon,
} from '#/components/icons/Settings'
import {
  UserCircle_Filled_Corner0_Rounded as UserCircleFilledIcon,
  UserCircle_Stroke2_Corner0_Rounded as UserCircleIcon,
} from '#/components/icons/UserCircle'
import {
  CENTER_COLUMN_OFFSET,
  CENTER_COLUMN_WIDTH,
} from '#/components/Layout/const'
import * as Menu from '#/components/Menu'
import * as Prompt from '#/components/Prompt'
import {Text} from '#/components/Typography'
import {useAgeAssurance} from '#/ageAssurance'
import {useAnalytics} from '#/analytics'
import {type Events} from '#/analytics/metrics/types'
import {IS_IOS, IS_NATIVE, IS_WEB} from '#/env'
import {isFollowingV2HomeDotEnabled} from '#/features/followingV2/eligibility'
import {useActorStatus} from '#/features/liveNow'
import {NEWS_V2_ENABLED} from '#/features/newsFeed/config'
import {type app} from '#/lexicons'
import {router} from '#/routes'
import {getComposeButtonLayout} from './composeButtonLayout'

const LARGE_ELEMENT_SIZE = 48
const NAV_ICON_WIDTH = 28
const NAV_ITEM_TO_TAB: Partial<
  Record<Events['nav:click']['item'], SidebarTab>
> = {
  home: 'Home',
  search: 'Search',
  chat: 'Messages',
  notifications: 'Notifications',
  profile: 'MyProfile',
  atmosphere: 'Atmosphere',
  feeds: 'Feeds',
  lists: 'Lists',
  saved: 'Bookmarks',
  settings: 'Settings',
}

export const LEFT_NAV_STANDARD_WIDTH = 240
export const LEFT_NAV_MINIMAL_WIDTH = 80
const LEFT_NAV_PWI_WIDTH = 245

function ProfileCard({minimal}: {minimal: boolean}) {
  const {currentAccount, accounts} = useSession()
  const {logoutEveryAccount} = useSessionApi()
  const {isLoading, data} = useProfilesQuery({
    handles: accounts.map(acc => acc.did),
  })
  const profiles = data?.profiles
  const signOutPromptControl = Prompt.usePromptControl()
  const {t: l} = useLingui()
  const t = useTheme()

  const profile = profiles?.find(p => p.did === currentAccount!.did)
  const otherAccounts = accounts
    .filter(acc => acc.did !== currentAccount!.did)
    .map(account => ({
      account,
      profile: profiles?.find(p => p.did === account.did),
    }))

  const {isActive: live} = useActorStatus(profile)

  return (
    <View style={[a.pb_md, !minimal && [a.w_full, a.align_start]]}>
      {!isLoading && profile ? (
        <Menu.Root>
          <Menu.Trigger label={l`Switch accounts`}>
            {({props, state, control}) => {
              const active = state.hovered || state.focused || control.isOpen
              return (
                <Button
                  label={props.accessibilityLabel}
                  {...props}
                  style={[
                    a.w_full,
                    a.transition_color,
                    active ? t.atoms.bg_contrast_25 : a.transition_delay_50ms,
                    a.rounded_full,
                    a.justify_between,
                    a.align_center,
                    a.flex_row,
                    {gap: 6},
                    !minimal && [a.pl_lg, a.pr_md],
                  ]}>
                  <View
                    style={[
                      !PlatformInfo.getIsReducedMotionEnabled() && [
                        a.transition_transform,
                        {transitionDuration: '250ms'},
                        !active && a.transition_delay_50ms,
                      ],
                      a.relative,
                      a.z_10,
                      active && {
                        transform: [
                          {scale: !minimal ? 2 / 3 : 0.8},
                          {translateX: !minimal ? -22 : 0},
                        ],
                      },
                    ]}>
                    <UserAvatar
                      avatar={profile.avatar}
                      size={LARGE_ELEMENT_SIZE}
                      type={profile?.associated?.labeler ? 'labeler' : 'user'}
                      live={live}
                    />
                  </View>
                  {!minimal && (
                    <>
                      <View
                        style={[
                          a.flex_1,
                          a.transition_opacity,
                          !active && a.transition_delay_50ms,
                          {
                            marginLeft: tokens.space.xl * -1,
                            opacity: active ? 1 : 0,
                          },
                        ]}>
                        <Text
                          emoji
                          style={[a.font_bold, a.text_sm, a.leading_snug]}
                          numberOfLines={1}>
                          {sanitizeDisplayName(
                            profile.displayName || profile.handle,
                          )}
                        </Text>
                        <Text
                          style={[
                            a.text_xs,
                            a.leading_snug,
                            t.atoms.text_contrast_medium,
                          ]}
                          numberOfLines={1}>
                          {sanitizeHandle(profile.handle, '@')}
                        </Text>
                      </View>
                      <EllipsisIcon
                        aria-hidden={true}
                        style={[
                          t.atoms.text_contrast_medium,
                          a.transition_opacity,
                          {opacity: active ? 1 : 0},
                        ]}
                        size="sm"
                      />
                    </>
                  )}
                </Button>
              )
            }}
          </Menu.Trigger>
          <SwitchMenuItems
            accounts={otherAccounts}
            signOutPromptControl={signOutPromptControl}
          />
        </Menu.Root>
      ) : (
        <LoadingPlaceholder
          width={LARGE_ELEMENT_SIZE}
          height={LARGE_ELEMENT_SIZE}
          style={[a.rounded_full, !minimal && a.ml_lg]}
        />
      )}
      <Prompt.Basic
        control={signOutPromptControl}
        title={l`Sign out?`}
        description={l`You will be signed out of all your accounts.`}
        onConfirm={() => logoutEveryAccount('Settings')}
        confirmButtonCta={l`Sign out`}
        cancelButtonCta={l`Cancel`}
        confirmButtonColor="negative"
      />
    </View>
  )
}

function SwitchMenuItems({
  accounts,
  signOutPromptControl,
}: {
  accounts:
    | {
        account: SessionAccount
        profile?: app.bsky.actor.defs.ProfileViewDetailed
      }[]
    | undefined
  signOutPromptControl: DialogControlProps
}) {
  const {t: l} = useLingui()
  const {currentAccount} = useSession()
  const {onPressSwitchAccount, pendingDid} = useAccountSwitcher()
  const navigation = useNavigation<NavigationProp>()
  const {control} = Menu.useMenuContext()
  const currentRouteInfo = useNavigationState(state => {
    if (!state) {
      return {name: 'Home'}
    }
    return getCurrentRoute(state)
  })
  const profileLink = currentAccount ? makeProfileLink(currentAccount) : '/'
  const isCurrent = isCurrentProfileRoute({
    routeName: currentRouteInfo.name,
    profileName:
      currentRouteInfo.name === 'Profile'
        ? (currentRouteInfo.params as CommonNavigatorParams['Profile']).name
        : undefined,
    currentHandle: currentAccount?.handle,
  })
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const closeEverything = useCloseAllActiveElements()

  const onAddAnotherAccount = () => {
    setShowLoggedOut(true)
    closeEverything()
  }

  const onProfilePress = (e: GestureResponderEvent) => {
    if (IS_WEB) {
      const mouseEvent = e as unknown as React.MouseEvent<
        HTMLAnchorElement,
        MouseEvent
      >
      if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.altKey) {
        return
      }
      mouseEvent.preventDefault()
    }

    const onNavigate = () => {
      if (isCurrent) {
        emitSoftReset()
      } else {
        const [screen, params] = router.matchPath(profileLink)
        // @ts-expect-error TODO: type matchPath well enough that it can be plugged into navigation.navigate directly
        navigation.navigate(screen, params, {pop: true})
      }
    }

    if (IS_IOS) {
      onNavigate()
    } else if (IS_NATIVE) {
      control.close()
      onNavigate()
    } else {
      control.close(onNavigate)
    }
  }

  return (
    <Menu.Outer>
      {accounts && accounts.length > 0 && (
        <>
          <Menu.LabelText>
            <Trans>Switch account</Trans>
          </Menu.LabelText>
          <Menu.Group>
            {/* Native groups require Menu.Item as a direct child. */}
            {accounts.map(({account, profile}) => (
              <Menu.Item
                key={account.did}
                disabled={!!pendingDid}
                style={[a.gap_sm, {minWidth: 150}]}
                label={l`Switch to ${sanitizeHandle(
                  profile?.handle ?? account.handle,
                  '@',
                )}`}
                onPress={() =>
                  void onPressSwitchAccount(account, 'SwitchAccount')
                }>
                <SwitchMenuItemAvatar profile={profile} />
                <Menu.ItemText>
                  {sanitizeHandle(profile?.handle ?? account.handle, '@')}
                </Menu.ItemText>
              </Menu.Item>
            ))}
          </Menu.Group>
          <Menu.Divider />
        </>
      )}
      <Menu.Group>
        <Menu.Item
          label={l`Go to profile`}
          onPress={onProfilePress}
          // @ts-expect-error href is web-only -prf
          href={profileLink}>
          <Menu.ItemIcon icon={UserCircleIcon} />
          <Menu.ItemText>
            <Trans>Go to profile</Trans>
          </Menu.ItemText>
        </Menu.Item>
        <Menu.Item label={l`Add another account`} onPress={onAddAnotherAccount}>
          <Menu.ItemIcon icon={PlusIcon} />
          <Menu.ItemText>
            <Trans>Add another account</Trans>
          </Menu.ItemText>
        </Menu.Item>
        <Menu.Item label={l`Sign out`} onPress={signOutPromptControl.open}>
          <Menu.ItemIcon icon={LeaveIcon} />
          <Menu.ItemText>
            <Trans>Sign out</Trans>
          </Menu.ItemText>
        </Menu.Item>
      </Menu.Group>
    </Menu.Outer>
  )
}

function SwitchMenuItemAvatar({
  profile,
}: {
  profile: app.bsky.actor.defs.ProfileViewDetailed | undefined
}) {
  const {isActive: live} = useActorStatus(profile)

  return (
    <View>
      <UserAvatar
        avatar={profile?.avatar}
        size={20}
        type={profile?.associated?.labeler ? 'labeler' : 'user'}
        live={live}
        hideLiveBadge
      />
    </View>
  )
}

interface NavItemProps {
  count?: string
  hasNew?: boolean
  href: string
  icons: {
    inactive: React.ComponentType<SVGIconProps>
    active: React.ComponentType<SVGIconProps>
  }
  label: string
  minimal: boolean
  navItem: Events['nav:click']['item']
  /** Other routes that belong to this item's section, for its active state. */
  relatedRoutes?: string[]
  onNavigateTab?: (tab: SidebarTab) => void
}
/** Every route of the news hub, so its nav item stays lit across its tabs. */
const NEWS_HUB_ROUTES = [
  'NewsFeed',
  'NewsSettings',
  'OutletDirectory',
  'Frontpage',
  'NewsArticle',
]

function NavItem({
  count,
  hasNew,
  href,
  icons,
  label,
  minimal,
  navItem,
  relatedRoutes,
  onNavigateTab,
}: NavItemProps) {
  const t = useTheme()
  const {t: l} = useLingui()
  const ax = useAnalytics()
  const {currentAccount} = useSession()

  const [pathName] = useMemo(() => router.matchPath(href), [href])
  const navigationState = useNavigationState(state => state)
  const currentRouteInfo = getCurrentRoute(navigationState)
  const tab = NAV_ITEM_TO_TAB[navItem]
  const isCurrent =
    navItem === 'profile'
      ? isCurrentProfileRoute({
          routeName: currentRouteInfo.name,
          profileName:
            currentRouteInfo.name === 'Profile'
              ? (currentRouteInfo.params as CommonNavigatorParams['Profile'])
                  .name
              : undefined,
          currentHandle: currentAccount?.handle,
        })
      : isTab(currentRouteInfo.name, pathName)
  const isSelected =
    IS_NATIVE && tab
      ? getTabState(navigationState, tab) !== TabState.Outside
      : isCurrent
  const isRelated =
    currentRouteInfo.name.startsWith(pathName) ||
    !!relatedRoutes?.includes(currentRouteInfo.name)
  const navigation = useNavigation<NavigationProp>()
  const onPressWrapped = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      ax.metric('nav:click', {item: navItem, surface: 'leftNav'})
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return
      }
      e.preventDefault?.()
      if (tab && onNavigateTab) {
        onNavigateTab(tab)
        return
      }
      if (isCurrent) {
        emitSoftReset()
      } else {
        const [screen, params] = router.matchPath(href)
        // @ts-expect-error TODO: type matchPath well enough that it can be plugged into navigation.navigate directly
        navigation.navigate(screen, params, {pop: true})
      }
    },
    [navigation, href, isCurrent, ax, navItem, onNavigateTab, tab],
  )

  const Icon =
    isSelected || (!IS_NATIVE && isRelated) ? icons.active : icons.inactive

  return (
    <PressableWithHover
      style={[
        a.flex_row,
        a.align_center,
        a.p_md,
        a.rounded_full,
        a.gap_sm,
        a.outline_inset_1,
        a.transition_color,
      ]}
      hoverStyle={t.atoms.bg_contrast_25}
      // @ts-expect-error the function signature differs on web -prf
      onPress={onPressWrapped}
      href={href}
      dataSet={{noUnderline: 1}}
      role="link"
      accessibilityLabel={label}
      accessibilityState={IS_NATIVE ? {selected: isSelected} : undefined}
      accessibilityHint="">
      <View
        style={[
          a.align_center,
          a.justify_center,
          {
            width: 24,
            height: 24,
          },
          a.z_10,
        ]}>
        <Icon aria-hidden={true} width={NAV_ICON_WIDTH} style={t.atoms.text} />
        {typeof count === 'string' && count ? (
          <View
            style={[
              a.absolute,
              a.inset_0,
              {right: -20}, // more breathing room
            ]}>
            <Text
              accessibilityLabel={l`${plural(count, {
                one: '# unread item',
                other: '# unread items',
              })}`}
              accessibilityHint=""
              accessible={true}
              numberOfLines={1}
              style={[
                a.absolute,
                a.text_xs,
                a.font_semi_bold,
                a.rounded_full,
                a.text_center,
                a.leading_tight,
                a.z_20,
                {
                  top: '-10%',
                  left: count.length === 1 ? 12 : 8,
                  backgroundColor: t.palette.primary_500,
                  color: t.palette.white,
                  lineHeight: a.text_sm.fontSize,
                  paddingHorizontal: 4,
                  paddingVertical: 1,
                  minWidth: 16,
                },
              ]}>
              {count}
            </Text>
          </View>
        ) : hasNew ? (
          <View
            style={[
              a.absolute,
              a.rounded_full,
              a.z_20,
              {
                backgroundColor: t.palette.primary_500,
                width: 8,
                height: 8,
                right: -2,
                top: -4,
              },
            ]}
          />
        ) : null}
      </View>
      {!minimal && (
        <Text style={[a.text_xl, isSelected ? a.font_bold : a.font_normal]}>
          {label}
        </Text>
      )}
    </PressableWithHover>
  )
}

function ComposeBtn({minimal}: {minimal: boolean}) {
  const {currentAccount} = useSession()
  const {getState} = useNavigation()
  const {openComposer} = useOpenComposer()
  const {t: l} = useLingui()
  const [isFetchingHandle, setIsFetchingHandle] = useState(false)
  const fetchHandle = useFetchHandle()

  const getProfileHandle = async () => {
    const routes = getState()?.routes
    const currentRoute = routes?.[routes?.length - 1]

    if (currentRoute?.name === 'Profile') {
      let handle: string | undefined = (
        currentRoute.params as CommonNavigatorParams['Profile']
      ).name

      if (handle.startsWith('did:')) {
        try {
          setIsFetchingHandle(true)
          handle = await fetchHandle(handle)
        } catch (e) {
          handle = undefined
        }
        setIsFetchingHandle(false)
      }

      if (
        !handle ||
        handle === currentAccount?.handle ||
        isInvalidHandle(handle)
      )
        return undefined

      return handle
    }

    return undefined
  }

  const onPressCompose = async () =>
    openComposer({mention: await getProfileHandle(), logContext: 'Fab'})

  const layout = getComposeButtonLayout({minimal, isNative: IS_NATIVE})

  return (
    <View style={layout.container}>
      <Button
        disabled={isFetchingHandle}
        label={l`Compose new post`}
        onPress={() => void onPressCompose()}
        size="large"
        color="primary"
        style={layout.button}>
        <ButtonIcon icon={EditBigIcon} size={minimal ? 'lg' : 'sm'} />
        {!minimal && (
          <ButtonText>
            <Trans context="action">New post</Trans>
          </ButtonText>
        )}
      </Button>
    </View>
  )
}

export function DesktopLeftNav({
  routeName,
  onNavigateTab,
}: {
  routeName: string
  onNavigateTab?: (tab: SidebarTab) => void
}) {
  const {hasSession, currentAccount} = useSession()
  const {i18n, t: l} = useLingui()
  const ax = useAnalytics()
  const {gtMobile} = useBreakpoints()

  const aa = useAgeAssurance()
  // splitview uses the minimal variant of the leftnav. unfortunately there's no easy
  // way to thread this data through because of the view hierarchy, so just check the route name
  const isMessagesRelatedScreen =
    !IS_NATIVE &&
    routeName.startsWith('Messages') &&
    aa.state.access === aa.Access.Full
  const {leftNavMinimal: leftNavMinimalBreakpoint, centerColumnOffset} =
    useLayoutBreakpoints()
  const numUnreadNotifications = useUnreadNotifications()
  const numUnreadMessages = useUnreadMessageCount()
  const isModerationInboxEnabled = ax.features.enabled(
    ax.features.ModerationInboxEnable,
  )
  const moderationInboxUnreadCountQuery = useModerationInboxUnreadCountQuery(
    isModerationInboxEnabled,
  )
  const moderationInboxUnreadCount = isModerationInboxEnabled
    ? (moderationInboxUnreadCountQuery.data?.unreadCounts.total ?? 0)
    : 0
  const numUnreadModerationInbox =
    moderationInboxUnreadCount > 0
      ? moderationInboxUnreadCount >= UNREAD_NOTIFICATION_CAP
        ? l({
            message: `${i18n.number(UNREAD_NOTIFICATION_CAP - 1)}+`,
            comment:
              'Displayed when the number of notifications exceeds the cap – for example, 99+',
          })
        : i18n.number(moderationInboxUnreadCount)
      : undefined
  const hasHomeBadge = useHomeBadge()
  const isAtmosphereExploreEnabled = ax.features.enabled(
    ax.features.AtmosphereScreenEnable,
  )
  const insets = useSafeAreaInsets()

  const leftNavMinimal = isMessagesRelatedScreen || leftNavMinimalBreakpoint

  if (!hasSession && !gtMobile) {
    return null
  }

  const content = (
    <>
      {hasSession ? (
        <ProfileCard minimal={leftNavMinimal} />
      ) : !leftNavMinimal ? (
        <View style={[a.pt_xl]}>
          <NavSignupCard />
        </View>
      ) : null}
      {hasSession && (
        <>
          <NavItem
            label={l`Home`}
            href="/"
            navItem="home"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            hasNew={hasHomeBadge && isFollowingV2HomeDotEnabled(ax)}
            icons={{
              inactive: HomeIcon,
              active: HomeFilledIcon,
            }}
          />
          <NavItem
            label={l`News`}
            href={NEWS_V2_ENABLED ? '/news/explore' : '/news'}
            navItem="news"
            relatedRoutes={NEWS_V2_ENABLED ? NEWS_HUB_ROUTES : undefined}
            minimal={leftNavMinimal}
            icons={{
              inactive: NewspaperIcon,
              active: NewspaperFilledIcon,
            }}
          />
          <NavItem
            label={l`Explore`}
            href="/search"
            navItem="search"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            icons={{
              inactive: MagnifyingGlassIcon,
              active: MagnifyingGlassFilledIcon,
            }}
          />
          <NavItem
            label={l`Notifications`}
            href="/notifications"
            navItem="notifications"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            count={numUnreadNotifications}
            icons={{inactive: BellIcon, active: BellFilledIcon}}
          />
          {isAtmosphereExploreEnabled && (
            <NavItem
              label={l`Atmosphere`}
              href="/atmosphere"
              navItem="atmosphere"
              onNavigateTab={onNavigateTab}
              minimal={leftNavMinimal}
              icons={{
                inactive: EarthIcon,
                active: EarthIcon,
              }}
            />
          )}
          <NavItem
            label={l`Chat`}
            href="/messages"
            navItem="chat"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            count={
              aa.flags.chatDisabled ? undefined : numUnreadMessages.numUnread
            }
            hasNew={!aa.flags.chatDisabled && numUnreadMessages.hasNew}
            icons={{inactive: MessageIcon, active: MessageFilledIcon}}
          />
          <NavItem
            label={l`Feeds`}
            href="/feeds"
            navItem="feeds"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            icons={{inactive: HashtagIcon, active: HashtagFilledIcon}}
          />
          <NavItem
            label={l`Lists`}
            href="/lists"
            navItem="lists"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            icons={{inactive: ListIcon, active: ListFilledIcon}}
          />
          <NavItem
            label={l({message: 'Saved', context: 'link to bookmarks screen'})}
            href="/saved"
            navItem="saved"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            icons={{inactive: BookmarkIcon, active: BookmarkFilledIcon}}
          />
          <NavItem
            label={l`Profile`}
            href={makeProfileLink(currentAccount!)}
            navItem="profile"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            icons={{inactive: UserCircleIcon, active: UserCircleFilledIcon}}
          />
          <NavItem
            label={l`Settings`}
            href="/settings"
            navItem="settings"
            onNavigateTab={onNavigateTab}
            minimal={leftNavMinimal}
            count={numUnreadModerationInbox}
            icons={{inactive: SettingsIcon, active: SettingsFilledIcon}}
          />
          <ComposeBtn minimal={leftNavMinimal} />
        </>
      )}
    </>
  )

  return (
    <View
      role="navigation"
      pointerEvents={IS_NATIVE ? 'box-none' : undefined}
      style={[
        a.fixed,
        a.top_0,
        web(a.p_lg),
        styles.leftNav,
        native(a.bottom_0),
        !hasSession && !leftNavMinimal && {width: LEFT_NAV_PWI_WIDTH},
        leftNavMinimal && [
          {width: LEFT_NAV_MINIMAL_WIDTH},
          !IS_NATIVE && a.h_full,
          a.align_center,
          web(a.overflow_x_hidden),
        ],
        web({maxHeight: '100vh', overflowY: 'auto', scrollbarWidth: 'thin'}),
        {
          transform: [
            {
              translateX:
                -(CENTER_COLUMN_WIDTH / 2) +
                (centerColumnOffset ? CENTER_COLUMN_OFFSET : 0) +
                (!IS_NATIVE &&
                isMessagesRelatedScreen &&
                !leftNavMinimalBreakpoint
                  ? LEFT_NAV_MINIMAL_WIDTH - LEFT_NAV_STANDARD_WIDTH
                  : 0),
            },
            {translateX: '-100%'},
            ...a.scrollbar_offset.transform,
          ],
        },
      ]}>
      {IS_NATIVE ? (
        <ScrollView
          style={[a.flex_1, a.w_full]}
          contentContainerStyle={[
            a.p_lg,
            {
              paddingTop: insets.top + tokens.space.lg,
              paddingBottom: insets.bottom + tokens.space.lg,
            },
          ]}
          showsVerticalScrollIndicator={false}>
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  leftNav: {
    left: '50%',
    width: LEFT_NAV_STANDARD_WIDTH,
  },
})
